import * as Sentry from "@sentry/react";
import { isStaleChunkSyntaxErrorEvent, isCSSChunkLoadErrorEvent, isViteModuleImportErrorEvent, isLazyDefaultExportErrorEvent, isDestructuringUndefinedErrorEvent, isWebVitalsInternalErrorEvent } from "./utils/chunkRecovery.js";
import logger from "./utils/logger";



const SENTRY_DSN = process.env.VITE_SENTRY_DSN;
const ENVIRONMENT = process.env.VITE_ENV;
const RELEASE = process.env.VITE_RELEASE;
const GOOGLE_CHARTS_UI_MODULE_RE = /\/charts\/[^/]+\/js\/jsapi_compiled_ui_module\.js(?:$|[?#])/;
const ANONYMOUS_TOKEN_RE = /([#?&]token=)[^#&]*/gi;
const BROWSER_EXTENSION_URL_RE = /^(?:chrome|moz|safari|safari-web|ms-browser)-extension:\/\//i;

function redactAnonymousSurveyToken(value) {
    return typeof value === "string"
        ? value.replace(ANONYMOUS_TOKEN_RE, "$1[FILTERED]")
        : value;
}

function redactBreadcrumbUrls(breadcrumb) {
    if (!breadcrumb || typeof breadcrumb !== "object") {
        return breadcrumb;
    }

    const redacted = { ...breadcrumb };
    ["message", "url", "from", "to"].forEach((key) => {
        if (typeof redacted[key] === "string") {
            redacted[key] = redactAnonymousSurveyToken(redacted[key]);
        }
    });

    if (redacted.data && typeof redacted.data === "object") {
        redacted.data = Object.fromEntries(
            Object.entries(redacted.data).map(([key, value]) => [
                key,
                redactAnonymousSurveyToken(value),
            ]),
        );
    }

    return redacted;
}

function isGoogleChartsTextNodeMutationEvent(event) {
    const exceptions = event.exception?.values || [];

    return exceptions.some((exception) => {
        if (!/Unexpected type of text node\b/i.test(exception?.value || "")) {
            return false;
        }

        const frames = exception?.stacktrace?.frames || event.stacktrace?.frames || [];
        return frames.some((frame) => {
            const filename = frame?.filename || frame?.abs_path || "";
            return GOOGLE_CHARTS_UI_MODULE_RE.test(filename);
        });
    });
}

function getFrameLocations(frame) {
    return [frame?.filename, frame?.abs_path]
        .filter((value) => typeof value === "string" && value.length > 0);
}

function isBrowserExtensionFrame(frame) {
    return getFrameLocations(frame).some((location) => BROWSER_EXTENSION_URL_RE.test(location));
}

function collectStackFrames(event) {
    const frames = [];
    const exceptions = event.exception?.values || [];

    exceptions.forEach((exception) => {
        if (Array.isArray(exception?.stacktrace?.frames)) {
            frames.push(...exception.stacktrace.frames);
        }
    });

    if (Array.isArray(event.stacktrace?.frames)) {
        frames.push(...event.stacktrace.frames);
    }

    return frames;
}

function getUrlOrigin(value) {
    if (typeof value !== "string" || !value) {
        return null;
    }

    try {
        return new URL(value).origin;
    } catch {
        return null;
    }
}

function isSameOriginUrl(value, origin) {
    if (!origin) {
        return false;
    }

    try {
        return new URL(value).origin === origin;
    } catch {
        return false;
    }
}

function isApplicationFrame(frame, requestOrigin) {
    if (isBrowserExtensionFrame(frame)) {
        return false;
    }

    const locations = getFrameLocations(frame);

    if (frame?.in_app === true) {
        return true;
    }

    return locations.some((location) => (
        (/^\//.test(location) && !/^\/\//.test(location)) ||
        /^(?:app|vite|webpack):\/\//i.test(location) ||
        isSameOriginUrl(location, requestOrigin)
    ));
}

function isBrowserExtensionNoiseEvent(event) {
    const frames = collectStackFrames(event);

    if (!frames.some(isBrowserExtensionFrame)) {
        return false;
    }

    const requestOrigin = getUrlOrigin(event.request?.url);
    return !frames.some((frame) => isApplicationFrame(frame, requestOrigin));
}

function hasAntdValidationErrorShape(value) {
    return Boolean(
        value &&
        typeof value === "object" &&
        Array.isArray(value.errorFields) &&
        (
            Object.prototype.hasOwnProperty.call(value, "outOfDate") ||
            Object.prototype.hasOwnProperty.call(value, "values") ||
            Object.prototype.hasOwnProperty.call(value, "message")
        )
    );
}

function isAntdValidationRejectionEvent(event, hint) {
    if (hasAntdValidationErrorShape(hint?.originalException)) {
        return true;
    }

    const exceptions = event.exception?.values || [];

    return exceptions.some((exception) => {
        const errorMessage = exception?.value || "";
        const errorType = exception?.type || "";
        const mechanism = exception?.mechanism || {};
        const mechanismType = mechanism.type || "";
        const isUnhandledRejection =
            /unhandledrejection/i.test(errorType) ||
            /onunhandledrejection/i.test(mechanismType) ||
            /Object captured as promise rejection/i.test(errorMessage);

        if (!isUnhandledRejection) {
            return false;
        }

        if (
            /Object captured as promise rejection with keys:/i.test(errorMessage) &&
            /\berrorFields\b/i.test(errorMessage) &&
            /\boutOfDate\b/i.test(errorMessage)
        ) {
            return true;
        }

        return (
            hasAntdValidationErrorShape(mechanism.data) ||
            hasAntdValidationErrorShape(event.extra?.__serialized__) ||
            hasAntdValidationErrorShape(event.extra?.reason)
        );
    });
}

// Only initialize Sentry in production with a valid DSN
if (SENTRY_DSN && ENVIRONMENT === "production" && !/^\/pre-joining\/?$/.test(window.location.pathname)) {
    Sentry.init({
        dsn: SENTRY_DSN,
        environment: "production",
        release: RELEASE || undefined,

        // Privacy: do not send PII (IP, email, etc.)
        sendDefaultPii: false,

        // 100% error sampling, 10% performance sampling (matches backend)
        sampleRate: 1.0,
        tracesSampleRate: 0.1,

        integrations: [
            Sentry.browserTracingIntegration(),
            Sentry.breadcrumbsIntegration({
                console: true,
                dom: true,
                fetch: true,
                history: true,
                xhr: true,
            }),
        ],

        beforeSend(event, hint) {
            if (/^\/pre-joining\/?$/.test(window.location.pathname)) return null;
            const errorMessage =
                event.exception?.values?.[0]?.value || "";
            const errorType =
                event.exception?.values?.[0]?.type || "";

            // Drop common network noise and expected errors
            if (
                /Network Error/i.test(errorMessage) ||
                /Failed to fetch/i.test(errorMessage) ||
                /Load failed/i.test(errorMessage) ||
                /timeout/i.test(errorMessage) ||
                /ResizeObserver loop/i.test(errorMessage) ||
                /The user aborted a request/i.test(errorMessage) ||
                /AbortError/i.test(errorMessage) ||
                /AbortError/i.test(errorType) ||
                /signal is aborted/i.test(errorMessage)
            ) {
                return null;
            }

            // Drop stale chunk parse errors (old chunk URL returning HTML after deploy)
            if (isStaleChunkSyntaxErrorEvent(event)) {
                return null;
            }

            // Drop CSS chunk load errors (stale CSS after deploy)
            if (isCSSChunkLoadErrorEvent(event)) {
                return null;
            }

            // Drop Vite JS module import errors (stale chunk URL returns HTML after deploy)
            if (isViteModuleImportErrorEvent(event)) {
                return null;
            }

            // Drop React.lazy .default TypeError (stale chunk resolves to undefined after deploy)
            if (isLazyDefaultExportErrorEvent(event)) {
                return null;
            }

            // Drop destructuring TypeError (stale chunk import() resolves to undefined after deploy)
            // e.g. "Cannot destructure property 'onCLS' of 'undefined' as it is undefined."
            if (isDestructuringUndefinedErrorEvent(event)) {
                return null;
            }

            // Drop web-vitals internal TypeErrors from browsers that lack Array.prototype.at()
            // (Safari < 15.4, Chrome < 92). web-vitals v5 uses .at(-1) in its CLS/INP classes;
            // this is pure telemetry — failure never affects app functionality.
            if (isWebVitalsInternalErrorEvent(event)) {
                return null;
            }

            // Drop Google Charts SVG text-measurement failures caused by
            // browser translation/extension DOM mutation inside Google's code.
            if (isGoogleChartsTextNodeMutationEvent(event)) {
                return null;
            }

            // Drop browser-extension injected errors (for example MetaMask inpage.js)
            // when the stack has no HRP application frame.
            if (isBrowserExtensionNoiseEvent(event)) {
                return null;
            }

            // Drop Ant Design form validation objects leaked as unhandled promise
            // rejections. They are UI validation state, not runtime failures.
            if (isAntdValidationRejectionEvent(event, hint)) {
                return null;
            }

            // Drop 401 errors (expected session expiry — text match catches unhandled rejections
            // where mechanism.data.statusCode is not populated by Sentry)
            if (/status code 401/i.test(errorMessage)) {
                return null;
            }

            // Drop 403 errors (authorization denied — IDOR/permission checks; backend enforces
            // and logs these; frontend surfaces them as UI messages, not Sentry events)
            if (/status code 403/i.test(errorMessage)) {
                return null;
            }

            // Drop 404 errors (deleted/moved resources, stale links — expected client error)
            if (/status code 404/i.test(errorMessage)) {
                return null;
            }

            // Drop 429 errors (rate limit — transient, expected under load; native fetch SSE
            // callers produce "HTTP 429: " messages since they bypass Axios retry handling)
            if (/HTTP 429/i.test(errorMessage) || /status code 429/i.test(errorMessage)) {
                return null;
            }

            // Drop 5xx backend errors (transient or expected server-side failures — already captured
            // by backend Sentry with full server context; duplicate frontend events add no value)
            if (/status code 5\d\d/i.test(errorMessage)) {
                return null;
            }
            // Native-fetch / SSE callers produce "HTTP NNN: ..." messages (no Axios wrapper)
            if (/\bHTTP 5\d\d\b/i.test(errorMessage)) {
                return null;
            }

            // Drop 401/403/404/429/5xx errors via structured status code (supplemental guard)
            const statusCode =
                event.exception?.values?.[0]?.mechanism?.data?.statusCode ||
                event.contexts?.response?.status_code;
            if (statusCode === 401 || statusCode === 403 || statusCode === 404 || statusCode === 429 || (statusCode >= 500 && statusCode <= 599)) {
                return null;
            }

            // Strip sensitive headers
            if (event.request?.headers) {
                delete event.request.headers.authorization;
                delete event.request.headers.Authorization;
                delete event.request.headers.cookie;
                delete event.request.headers.Cookie;
            }

            if (event.request?.url) {
                event.request.url = redactAnonymousSurveyToken(event.request.url);
            }

            if (Array.isArray(event.breadcrumbs)) {
                event.breadcrumbs = event.breadcrumbs.map(redactBreadcrumbUrls);
            }

            // Filter sensitive query parameters
            if (
                event.request?.query_string &&
                typeof event.request.query_string === "string"
            ) {
                const sensitiveParams = [
                    "token",
                    "password",
                    "api_key",
                    "secret",
                    "tokenR",
                ];
                sensitiveParams.forEach((param) => {
                    event.request.query_string =
                        event.request.query_string.replace(
                            new RegExp(`${param}=[^&]*`, "gi"),
                            `${param}=[FILTERED]`
                        );
                });
            }

            // Strip IP address (privacy) but keep user id/email set via Sentry.setUser()
            if (event.user) {
                delete event.user.ip_address;
            }

            return event;
        },

        beforeBreadcrumb(breadcrumb) {
            // Drop logger.debug noise; keep warn/error
            if (breadcrumb.category === "console") {
                if (
                    breadcrumb.level === "log" ||
                    breadcrumb.level === "debug"
                ) {
                    return null;
                }
            }
            return breadcrumb;
        },
    });

    logger.debug("Sentry initialized for production");
}

export default Sentry;
